import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, MapPin, Clock } from "lucide-react";
import { useLang } from "../../i18n/LanguageProvider";
import { AGENCY } from "../../i18n/dict";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: i * 0.1, ease: [0.76, 0, 0.24, 1] },
  }),
};

export const FinalCTA = () => {
  const { t } = useLang();

  return (
    <section
      id="contact"
      data-testid="cta-section"
      className="relative bg-[#0B1416] text-[#F5F1E8] py-32 md:py-44 lg:py-56 overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#0D7A7D]/25 blur-[160px] pointer-events-none" />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 text-center">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="overline text-[#6EE3E6]"
        >
          — {t.cta.overline}
        </motion.div>

        <h2
          data-testid="cta-title"
          className="font-heading-tight leading-[0.88] mt-8"
          style={{ fontSize: "clamp(56px, 11vw, 220px)", letterSpacing: "-0.045em" }}
        >
          {t.cta.title_lines.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span
                variants={lineReveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                custom={i}
                className="inline-block"
              >
                {i === 1 ? (
                  <span className="font-italic text-[#6EE3E6]">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-10 max-w-2xl mx-auto text-lg font-light opacity-75"
        >
          {t.cta.subtitle}
        </motion.p>

        {/* CTA buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-12 flex flex-wrap justify-center gap-3"
        >
          <a
            href={`tel:${AGENCY.phone_raw}`}
            data-testid="cta-call"
            className="tt-btn tt-btn-primary"
          >
            <Phone size={13} />
            {t.cta.call}
          </a>
          <a
            href={`https://wa.me/${AGENCY.whatsapp}`}
            target="_blank"
            rel="noreferrer"
            data-testid="cta-whatsapp"
            className="tt-btn tt-btn-ghost"
          >
            <MessageCircle size={13} />
            {t.cta.whatsapp}
          </a>
          <a
            href={`mailto:${AGENCY.email}`}
            data-testid="cta-email"
            className="tt-btn tt-btn-ghost"
          >
            <Mail size={13} />
            {t.cta.email}
          </a>
          <a
            href="https://share.google/NJU5fiHBcGWLyvcZU"
            target="_blank"
            rel="noreferrer"
            data-testid="cta-directions"
            className="tt-btn tt-btn-ghost"
          >
            <MapPin size={13} />
            Google Maps
          </a>
        </motion.div>

        {/* Contact details */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-16 max-w-4xl mx-auto text-left border-t border-[#F5F1E8]/12 pt-16"
        >
          <div>
            <div className="overline text-[#6EE3E6] mb-4">
              <MapPin size={12} className="inline mr-2 -mt-0.5" />
              {t.footer.address_label}
            </div>
            <div className="font-heading text-2xl leading-tight">
              {AGENCY.address}
              <br />
              {AGENCY.address2}
            </div>
            <a
              href="https://share.google/NJU5fiHBcGWLyvcZU"
              target="_blank"
              rel="noreferrer"
              data-testid="cta-maps-link"
              className="inline-flex items-center gap-2 mt-4 text-xs uppercase tracking-[0.28em] text-[#6EE3E6] border-b border-[#6EE3E6]/40 pb-1 hover:border-[#6EE3E6] transition-colors"
            >
              Ver en Google Maps →
            </a>
          </div>
          <div>
            <div className="overline text-[#6EE3E6] mb-4">
              <Phone size={12} className="inline mr-2 -mt-0.5" />
              {t.footer.contact_label}
            </div>
            <div className="font-heading text-2xl leading-tight">
              {AGENCY.phone}
              <br />
              <span className="text-base opacity-70 font-light">{AGENCY.email}</span>
            </div>
          </div>
          <div>
            <div className="overline text-[#6EE3E6] mb-4">
              <Clock size={12} className="inline mr-2 -mt-0.5" />
              {t.footer.hours}
            </div>
            <div className="font-heading text-2xl leading-tight">
              {t.footer.hours_val}
            </div>
          </div>
        </motion.div>

        {/* Slogan */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 1.1 }}
          className="mt-24 font-italic text-2xl md:text-3xl opacity-70"
        >
          « {t.cta.slogan} »
        </motion.div>
      </div>
    </section>
  );
};
